/**
 * What this person holds, and for an administrator who else holds what.
 *
 * `me` is read once the session is established, and every screen adapts from
 * it: it is only ever a hint about what to show. The server has the last word
 * on every request, so a stale `me` can hide a button but never grant a power.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as api from '@/api/access'
import { ApiError, NetworkError } from '@/api/http'
import type { AccessUser, Capabilities, Direction, Identity, Me } from '@/domain/access'
import { t } from '@/i18n'

const NOTHING: Capabilities = {
  seesAll: false,
  approves: false,
  managesAccess: false,
  writesEverywhere: false,
}

export const useAccessStore = defineStore('access', () => {
  const me = ref<Me | null>(null)
  const directions = ref<Direction[]>([])
  const users = ref<AccessUser[]>([])
  const loading = ref(false)
  const busy = ref(false)
  const problem = ref<string | null>(null)

  /** What this person may do. Nothing until it is known, never more. */
  const capabilities = computed(() => me.value?.capabilities ?? NOTHING)

  function describe(error: unknown): string {
    if (error instanceof ApiError) return error.message
    if (error instanceof NetworkError) return t('common.unreachable')
    return t('common.problem')
  }

  /** Read who this person is to the builder. Safe to call again. */
  async function loadMe(): Promise<void> {
    try {
      me.value = await api.getMe()
    } catch (error) {
      problem.value = describe(error)
    }
  }

  async function loadDirections(): Promise<void> {
    try {
      directions.value = await api.listDirections()
    } catch (error) {
      problem.value = describe(error)
    }
  }

  /** Everything the access screen shows: the people, and the directions to give them. */
  async function load(): Promise<void> {
    loading.value = true
    problem.value = null
    try {
      const [people, parts] = await Promise.all([api.listUsers(), api.listDirections()])
      users.value = people
      directions.value = parts
    } catch (error) {
      problem.value = describe(error)
    } finally {
      loading.value = false
    }
  }

  /** Run one change, saying what went wrong if it did. */
  async function change<T>(work: () => Promise<T>): Promise<T | null> {
    busy.value = true
    problem.value = null
    try {
      return await work()
    } catch (error) {
      problem.value = describe(error)
      return null
    } finally {
      busy.value = false
    }
  }

  /** Make these what somebody holds. Adds them to the list if they were not on it. */
  async function grant(
    sub: string,
    held: {
      profiles: readonly string[]
      directions: readonly string[]
      consultDirections?: readonly string[]
    },
    who: Identity = {},
  ): Promise<boolean> {
    const saved = await change(() => api.setAccess(sub, held, who))
    if (!saved) return false
    users.value = [...users.value.filter((one) => one.sub !== saved.sub), saved]
    // Changing your own standing changes what this screen may show you next.
    if (saved.sub === me.value?.id) await loadMe()
    return true
  }

  async function revoke(sub: string): Promise<boolean> {
    const removed = await change(() => api.removeUser(sub))
    if (removed) {
      users.value = users.value.filter((one) => one.sub !== sub)
      if (sub === me.value?.id) await loadMe()
    }
    return removed === true
  }

  return {
    me,
    directions,
    users,
    loading,
    busy,
    problem,
    capabilities,
    loadMe,
    loadDirections,
    load,
    grant,
    revoke,
  }
})
